import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { useIntl } from 'react-intl';
import useSearch from 'src/registry/hooks/useSearch';
import { createColumnHelper } from '@tanstack/react-table';
import { commonMessages, generalMessages } from 'src/utils/messages';
import useFilters from 'src/registry/hooks/useFilters';
import { useListPayments } from 'src/features/payments/api/useListPayments';
import { Payment } from 'src/features/payments/utils/types';
import { paymentsMessages } from '../../utils/messages';
import { CellLinkWithIcon } from 'src/registry/components/table/cells/CellLinkWithIcon';
import { customersMessages } from 'src/features/customers/utils/messages';
import { PAYMENT_STATUS_BADGE_OPTIONS, PAYMENT_TYPE_BADGE_OPTIONS } from 'src/features/payments/utils/constants';
import { financialDocumentsMessages } from 'src/features/financialDocuments/utils/messages';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useSorting from 'src/registry/hooks/useSorting';
import usePagination from 'src/registry/hooks/usePagination';
import CellPaymentActions from 'src/features/payments/components/tables/CellPaymentActions';
import CreatePaymentFormModal from 'src/features/payments/components/forms/CreatePaymentFormModal';
import { Link, useParams } from 'react-router-dom';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

type PaymentsTableProps = {
    item_id?: string;
    item_type?: string;
};

const PaymentsTable = ({ item_id, item_type }: PaymentsTableProps) => {
    const { formatMessage, formatNumber } = useIntl();
    const { subscriptionId = '', customerId = '' } = useParams();

    const permissionManagePayments = useHasPermissions([PERMISSION.MANAGE_PAYMENTS]);
    const permissionAccessCustomers = useHasPermissions([PERMISSION.ACCESS_CUSTOMERS]);
    const permissionAccessFinancialDocuments = useHasPermissions([PERMISSION.ACCESS_FINANCIAL_DOCUMENTS]);

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'name' });
    const { queryFiltersOptions, ...filters } = useFilters();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'date', desc: true }],
    });

    const {
        data: paymentsData,
        isLoading,
        isError,
    } = useListPayments({
        ...querySearchOptions,
        ...queryFiltersOptions,
        ...querySortingOptions,
        ...queryPaginationOptions,
        ...(item_type && { item_type: item_type }),
        ...(item_id && { item_id: item_id }),
        ...(customerId && { customer: customerId }),
    });

    const columnHelper = createColumnHelper<Payment>();

    const columns = [
        columnHelper.accessor('date', {
            header: DataTableColumnHeader,
            // A payment has no name, so the date doubles as the row title that opens its dashboard.
            cell: (ctx) => (
                <CellLinkWithIcon
                    {...ctx}
                    to={`/payments/${ctx.row.original.id}`}
                    render={() => <CellDatetime {...ctx} />}
                />
            ),
            enableSorting: true,
            meta: {
                title: formatMessage(commonMessages.date),
            },
        }),
        columnHelper.accessor('money_account.name', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(paymentsMessages.moneyAccount),
            },
        }),
        columnHelper.accessor('customer.name', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => {
                const customerId = ctx.row.original.customer?.id;
                return (
                    <CellLinkWithIcon
                        {...ctx}
                        to={`/customers/${customerId}`}
                        hasPermissions={permissionAccessCustomers}
                    />
                );
            },
            meta: {
                title: formatMessage(customersMessages.customer),
            },
        }),
        columnHelper.accessor('financial_documents', {
            header: DataTableColumnHeader,
            enableHiding: true,
            // A payment can be allocated across several documents, so render each one as its
            // own link rather than a single value.
            cell: ({ row }) => {
                const documents = row.original.financial_documents ?? [];
                if (!documents.length) return '-';

                return (
                    <div className="flex flex-wrap gap-x-1">
                        {documents.map((document, index) => (
                            <span key={document.id}>
                                {permissionAccessFinancialDocuments ? (
                                    <Link
                                        to={`/financial-documents/${document.id}`}
                                        className="text-primary-700 font-medium"
                                    >
                                        {document.number}
                                    </Link>
                                ) : (
                                    document.number
                                )}
                                {index < documents.length - 1 && ','}
                            </span>
                        ))}
                    </div>
                );
            },
            meta: {
                title: formatMessage(financialDocumentsMessages.financialDocument),
            },
        }),
        columnHelper.accessor('type', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            enableSorting: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(paymentsMessages.type),
                badgeOptions: PAYMENT_TYPE_BADGE_OPTIONS,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
        }),
        columnHelper.accessor('status', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => {
                return ctx.row.original.status !== null ? <CellBadge {...ctx} /> : '-';
            },
            meta: {
                title: formatMessage(generalMessages.status),
                badgeOptions: PAYMENT_STATUS_BADGE_OPTIONS,
            },
        }),
        columnHelper.accessor('amount', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                return formatNumber(row.original.amount / 100, {
                    style: 'currency',
                    currency: row.original.currency,
                });
            },
            meta: {
                title: formatMessage(paymentsMessages.amount),
            },
        }),
        columnHelper.accessor('refunded_amount', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                return row.original?.refunded_amount
                    ? formatNumber(row.original.refunded_amount / 100, {
                          style: 'currency',
                          currency: row.original.currency,
                      })
                    : '-';
            },
            meta: {
                title: formatMessage(paymentsMessages.refunded),
            },
        }),
        columnHelper.accessor('allocated_amount', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                return row.original?.allocated_amount
                    ? formatNumber(row.original.allocated_amount / 100, {
                          style: 'currency',
                          currency: row.original.currency,
                      })
                    : '-';
            },
            meta: {
                title: formatMessage(paymentsMessages.allocatedAmount),
            },
        }),
        columnHelper.accessor('remaining_amount', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                return row.original?.remaining_amount
                    ? formatNumber(row.original.remaining_amount / 100, {
                          style: 'currency',
                          currency: row.original.currency,
                      })
                    : '-';
            },
            meta: {
                title: formatMessage(paymentsMessages.remainingAmount),
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellPaymentActions,
            maxSize: 45,
        }),
    ];

    return (
        <DataTable<Payment, unknown>
            columns={columns}
            data={paymentsData}
            semantics={{
                title: formatMessage(paymentsMessages.payments),
                entryName: formatMessage(paymentsMessages.payment).toLowerCase(),
                entryNames: formatMessage(paymentsMessages.payments).toLowerCase(),
                placeholder: formatMessage(paymentsMessages.payment).toLowerCase(),
            }}
            search={search}
            sorting={sorting}
            pagination={pagination}
            filters={filters}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                newEntry: permissionManagePayments && !subscriptionId ? CreatePaymentFormModal : undefined,
            }}
        />
    );
};

export default PaymentsTable;
